// PATH: src/components/shared/AuthModal.jsx
// Import Dependencies
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// Local Imports
import { useAuthContext } from "app/contexts/auth/context";
import { useAuthModalContext } from "app/contexts/authModal/context";
import {
  cancelOneTap,
  initGoogleIdentity,
  readGoogleCredential,
  renderGoogleButton,
  setCredentialHandler,
  showOneTap,
} from "utils/googleIdentity";
import "./auth-modal.css";

// ----------------------------------------------------------------------
// Login modal — NO Firebase. Uses Google Identity Services directly:
//   - One Tap card (no separate window) is shown as soon as the modal opens;
//     returning users with ONE Google account are signed in automatically,
//     with several accounts they pick one in that card.
//   - The official "Continue with Google" button is the fallback.
// Both hand back a Google ID token to handleCredential() below.
//
// Flow (all in one background pass, nothing is asked from the user):
//   1. Decode the Google token -> email / socialId / name / photo.
//   2. Try login. code 200 -> done.
//   3. code 100 (no account yet) -> register in the background, log in again.
//   4. On success: close the modal and run onSuccess().

export function AuthModal() {
  const { isOpen, label, onSuccess, closeAuthModal } = useAuthModalContext();
  const { googleLogin, googleRegister } = useAuthContext();

  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [gsiError, setGsiError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  const buttonRef = useRef(null);
  const busyRef = useRef(false);
  // true once Google handed us a credential. After that we must NOT call
  // cancel() on the One Tap prompt (Google treats a cancelled auto sign-in as
  // "user said no" and switches auto sign-in off for a day).
  const credentialRef = useRef(false);

  const handleCredential = async (response) => {
    if (busyRef.current) return; // ignore double callbacks
    busyRef.current = true;
    credentialRef.current = true;
    setLoading(true);
    setStatusText("Signing you in…");

    try {
      const g = readGoogleCredential(response?.credential);

      let loginRes = await googleLogin({
        email: g.email,
        socialId: g.socialId,
        ProfileImg: g.photoUrl,
      });

      // Student not found -> create the account in the background with
      // only whatever data Google returned, then log in again.
      if (loginRes?.code === 100) {
        setStatusText("Setting up your account…");

        const regRes = await googleRegister({
          signupType: "social",
          socialId: g.socialId,
          email: g.email,
          ...(g.firstName ? { firstName: g.firstName } : {}),
          ...(g.lastName ? { lastName: g.lastName } : {}),
          ...(g.photoUrl ? { profileImg: g.photoUrl } : {}),
        });

        if (regRes?.code !== 200) {
          toast.error(regRes?.message || "Couldn't create your account. Please try again.");
          return;
        }

        setStatusText("Signing you in…");
        loginRes = await googleLogin({
          email: g.email,
          socialId: g.socialId,
          ProfileImg: g.photoUrl,
        });
      }

      if (loginRes?.code === 200) {
        toast.success(loginRes?.message || "Welcome to Raajneeti AI!");
        closeAuthModal();
        if (typeof onSuccess === "function") {
          onSuccess();
        } else {
          window.location.assign("/raajneeti/home");
        }
      } else {
        toast.error(loginRes?.message || "Sign-in failed. Please try again.");
      }
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      toast.error(error?.message || "Google sign-in failed. Please try again.");
    } finally {
      busyRef.current = false;
      setLoading(false);
      setStatusText("");
    }
  };

  // Always point Google's (one-time initialised) callback at the latest handler
  useEffect(() => {
    setCredentialHandler(handleCredential);
  });

  // Lock the page behind the modal (no background scrolling while it's open)
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Load Google, draw the official button, show One Tap
  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    let prompted = false;
    let promptTimer = null;

    credentialRef.current = false;
    setGsiError("");

    initGoogleIdentity()
      .then(() => {
        if (cancelled) return;

        if (buttonRef.current) {
          buttonRef.current.innerHTML = "";
          renderGoogleButton(buttonRef.current);
        }

        // Small delay: React StrictMode (dev) mounts -> unmounts -> mounts, and
        // we don't want the first throw-away mount to start (and then cancel)
        // the prompt.
        promptTimer = setTimeout(() => {
          if (cancelled) return;
          prompted = true;
          showOneTap();
        }, 250);
      })
      .catch((err) => {
        if (!cancelled) setGsiError(err?.message || "Couldn't start Google sign-in.");
      });

    return () => {
      cancelled = true;
      clearTimeout(promptTimer);
      // only remove the prompt if the user closed the modal WITHOUT signing in
      if (prompted && !credentialRef.current) cancelOneTap();
    };
  }, [isOpen, retryKey]);

  if (!isOpen) return null;

  // Clicking outside the box does NOTHING. The modal closes only through the
  // X button (disabled while a sign-in is in progress).
  const handleClose = () => {
    if (!loading) closeAuthModal();
  };

  return (
    <div className="rjm on">
      <div className="rjm-box rjm-auth relative">
        <button
          type="button"
          aria-label="Close"
          title="Close"
          onClick={handleClose}
          disabled={loading}
          className="absolute right-3.5 top-3.5 flex size-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="size-4.5"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="rjm-brand">
          Raajneeti <b>AI</b>
        </div>
        <h2>Continue to {label || "Learning PSIR"}</h2>
        <p>Sign in to open notes, practice, the trackers and AI समीक्षा.</p>

        {/* Google's official button (kept mounted, hidden while signing in) */}
        <div
          ref={buttonRef}
          style={{
            display: loading || gsiError ? "none" : "flex",
            justifyContent: "center",
            minHeight: 44,
          }}
        />

        {loading && (
          <button type="button" className="goog-btn" disabled>
            <span className="goog-g">G</span>
            {statusText || "Please wait…"}
          </button>
        )}

        {!loading && gsiError && (
          <>
            <p style={{ color: "#b91c1c", fontSize: 12.5, margin: "0 0 10px" }}>{gsiError}</p>
            <button type="button" className="goog-btn" onClick={() => setRetryKey((k) => k + 1)}>
              <span className="goog-g">G</span>
              Try again
            </button>
          </>
        )}

        <div className="rjm-fine">
          By continuing you agree to the Terms and Privacy Policy.
        </div>
      </div>
    </div>
  );
}